import { AntInput } from "@/CommonElements/AntInput";
import { Controllers } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { Button, Stack } from "react-bootstrap";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { Container, Spinner } from "reactstrap";
import { validateArabicAndNumbersOnly, validateEnglishAndNumbersOnly } from "../ProfileForm";
import { cn } from "@/utils/helpers";
import { Switch, Tag } from "antd";
import {
  useCreateHouseExaminationItemMutation,
  useGetOneHouseExaminationItemQuery,
  useUpdateHouseExaminationItemMutation,
} from "@/services/basicData";
import { useEffect } from "react";

export const HouseExaminationItemPage = () => {
  // ==============
  // hooks
  // ==============
  const { t } = useTranslation();
  const { mode } = useParams();
  const isViewMode = mode === "view";

  const form = useForm({
    defaultValues: {
      is_active: true,
    },
  });
  const {
    control,
    formState: { errors },
  } = form;

  const { handleSubmit, setError } = form;

  // ==============
  // Side Effects
  // =============

  const {
    mutate: createHouseExaminationItem,
    isPending: isCreateHouseExaminationItemPending,
  } = useCreateHouseExaminationItemMutation(setError);
  const {
    mutate: updateHouseExaminationItem,
    isPending: isUpdateHouseExaminationItemPending,
  } = useUpdateHouseExaminationItemMutation(setError);

  const onSubmit = (data) => {
    if (mode === "create") {
      createHouseExaminationItem(data, {
        onSuccess: () => {
          form.reset(form.formState.defaultValues);
        },
      });
    } else {
      updateHouseExaminationItem(data, {
        onSuccess: () => {
          form.reset(form.formState.defaultValues);
        },
      });
    }
  };

  const isPending =
    isCreateHouseExaminationItemPending || isUpdateHouseExaminationItemPending;

  const backLink =
    window.history.length > 1
      ? -1
      : `/app/makkah/basic-data/house-examination-items`;

  const title = {
    view: t("VIEW_HOUSE_EXAMINATION_ITEM"),
    create: t("CREATE_HOUSE_EXAMINATION_ITEM"),
    edit: t("UPDATE_HOUSE_EXAMINATION_ITEM"),
  };
  return (
    <Stack gap={2}>
      <PageHeader title={title[mode]}></PageHeader>
      <Container fluid>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Form */}
            <HouseExaminationItemForm control={control} errors={errors} />

            {/* Form Actions */}
            {isViewMode ? null : (
              <ActionsFooter>
                <Button
                  className="btn btn-primary"
                  type="submit"
                  disabled={isPending}
                >
                  {isPending ? <Spinner size="sm" /> : null}
                  {mode === "create" ? t("CREATE") : t("UPDATE")}
                </Button>
                <Link
                  disabled={isPending}
                  style={{ pointerEvents: isPending ? "none" : "auto" }}
                  to={backLink}
                  className="btn btn-danger"
                  type="submit"
                >
                  {t("CANCEL")}
                </Link>
              </ActionsFooter>
            )}
          </form>
        </FormProvider>
      </Container>
    </Stack>
  );
};

const HouseExaminationItemForm = () => {
  // ==============
  // hooks
  // ==============
  const form = useFormContext();
  const { id, mode } = useParams();
  const isViewMode = mode === "view";

  // ==============
  // Side Effects
  // ==============
  const { data: houseExaminationItemResponse, isLoading } =
    useGetOneHouseExaminationItemQuery({ id });
  const houseExaminationItem = houseExaminationItemResponse?.data?.data;

  useEffect(() => {
    if (houseExaminationItem) {
      form.reset(houseExaminationItem);
    }
  }, [houseExaminationItem]);

  const {
    control,
    formState: { errors },
  } = form;
  const { t } = useTranslation();

  const controllers = [
    {
      labelProps: {
        children: t("NAME_AR"),
      },
      controllerProps: {
        name: "name_ar",
        rules: {
          required: t("FIELD_REQUIRED"),
          validate: (value) => validateArabicAndNumbersOnly(value, t),
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            placeholder={t("NAME_AR")}
            status={errors.name_ar ? "error" : undefined}
          />
        ),
      },
      renderValue: (value) => value || "-",
    },
    {
      labelProps: {
        children: t("NAME_EN"),
      },
      controllerProps: {
        name: "name_en",
        rules: {
          required: t("FIELD_REQUIRED"),
          validate: (value) => validateEnglishAndNumbersOnly(value, t),
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            placeholder={t("NAME_EN")}
            status={errors.name_en ? "error" : undefined}
          />
        ),
      },
      renderValue: (value) => value || "-",
    },
    {
      labelProps: {
        children: t("STATUS"),
      },
      controllerProps: {
        name: "is_active",
        render: ({ field }) => (
          <div>
            <Switch
              {...field}
              checked={field.value}
              onChange={(checked) => field.onChange(checked)}
              status={errors.is_active ? "error" : undefined}
              checkedChildren={t("ACTIVE")}
              unCheckedChildren={t("INACTIVE")}
            />
          </div>
        ),
      },
      renderValue: (value) => (
        <div
          className={cn(
            "bg-transparent px-3 py-1 rounded-pill",
            value ? "badge badge-light-success border border-light-success" : "badge badge-light-danger border border-light-danger"
          )}
          style={{ fontSize: "12px" }}
        >
          {value ? t("ACTIVE") : t("INACTIVE")}
        </div>
      ),
    },
  ];

  return (
    <Controllers
      isLoading={isLoading}
      isViewMode={isViewMode}
      watch={form.watch}
      {...{
        controllers: controllers.map((config) => ({
          ...config,
          labelProps: {
            ...config.labelProps,
            className: cn("fw-bold required", config.labelProps.className),
          },
          controllerProps: {
            ...config.controllerProps,
            rules: {
              ...(config.controllerProps.rules || {}),
            },
            control,
            errors,
          },
          layout: {
            colProps: { xs: 6, md: 6 },
          },
        })),
      }}
    />
  );
};
